import './WIDGT_AIND_QUANT_02_WidgetLayout.css';

import { Box } from '@mui/material';
import useB2BTranslation from '../../../../b2b-common/hooks/useB2BTranslation';
import { toLocaleDateTime } from '../../../../b2b-common/helpers';
import WidgetQuantitativeChart from './charting/WidgetQuantitativeChart';
import { InfoOutlined } from '@mui/icons-material';
import CombinedStatusTrend from '../../../../b2b-plans/components/status-trend/CombinedStatusTrend';
import SimpleTimeTargetProgress from '../../../../b2b-plans/components/progress/SimpleTimeTargetProgress';
import { calculateTimeProgress } from '../../../../b2b-plans/components/helper';

const WidgetSampleValue = ({ objectiveData, sampleMeasurementUnit }) => {
    const lastSampleDate = objectiveData.lastSample.sampleDate;
    return (
        <Box className='widget-WIDGT_AIND_QUANT_02-sample-value-container'>
            <Box className='widget-WIDGT_AIND_QUANT_02-sample-value' display={'flex'} flexDirection={'row'} gap={0.5} alignItems={'center'}>
                <Box className='widget-WIDGT_AIND_QUANT_02-sample-value-number'
                    fontFamily={'Inter'} fontSize={'45px'} fontWeight={'bold'}
                >
                    {objectiveData.lastSample.quantitativeValue}
                </Box>
                <Box className='widget-WIDGT_AIND_QUANT_02-sample-measurement-unit'
                    fontFamily={'Inter'} fontSize={'10px'} fontWeight={'normal'}
                    color={'rgba(0, 0, 0, 0.6)'}
                    alignSelf={'flex-end'}
                    pb={'15px'}
                >
                    {sampleMeasurementUnit}
                </Box>
            </Box>
            <Box className='widget-WIDGT_AIND_QUANT_02-sample-date'
                fontFamily={'Inter'} fontSize={'10px'} fontWeight={'normal'}
                color={'rgba(0, 0, 0, 0.6)'}
            >
                {toLocaleDateTime(lastSampleDate, false)}
            </Box>
        </Box>
    );
};

const WidgetLabelValue = ({ label, value }) => {
    return (
        <div className='label-value-container'>
            <div className='label-value-label'>
                {label}
            </div>
            <div className='label-value-value'>
                {value}
            </div>
        </div>
    );
};

const WIDGT_AIND_QUANT_02_WidgetLayout = ({ areaIndicatorData, objectiveData, loading = false,
    atPublicDashboard = false, className = null }) => {
    const { t } = useB2BTranslation('b2b-reporting');

    if (loading) {
        return (
            <Box display={'flex'} justifyContent='center' alignItems='center' className={`widget-WIDGT_AIND_QUANT_02-loading ${className}`}>
                {t('Loading...')}
            </Box>
        );
    };

    const measurementUnit = areaIndicatorData?.indicatorDTO?.quantitativeMeasurementUnitDTO?.quantitativeMeasurementUnit || '';

    return (
        areaIndicatorData && objectiveData && (
            <Box className={`widget-WIDGT_AIND_QUANT_02 ${className}`}
                display={'flex'} flexDirection="row" gap={2} padding={'0px 5px'}
                bgcolor={'transparent'} borderRadius={2} width={'100%'} // border={'1px solid #ccc'}
                height={'170px'} boxSizing={'border-box'}
            >
                <Box className="widget-WIDGT_AIND_QUANT_02-left-content"
                    display={'flex'} flexDirection="column" gap={0.5} padding={'5px 5px'}
                    // bgcolor={'#f0f0f0'} borderRadius={2} border={'1px solid #ccc'}
                    width={'30%'} height={'100%'} boxSizing={'border-box'}
                >
                    <WidgetSampleValue
                        objectiveData={objectiveData}
                        sampleMeasurementUnit={measurementUnit}
                    />
                    <WidgetQuantitativeChart
                        objective={objectiveData}
                        showX={false}
                        showY={false}
                        grid={{ left: '3%', right: '3%', top: '0%', bottom: '1%', containLabel: true }}
                        className={'widget-WIDGT_AIND_QUANT_02-chart'}
                    />
                    <Box className='widget-WIDGT_AIND_QUANT_02-area-indicator-type'
                        display={'flex'} flexDirection={'row'} gap={0.5} alignItems={'center'}
                        fontFamily={'Inter'} fontSize={'10px'} fontWeight={'normal'}
                        color={'rgba(0, 0, 0, 0.6)'}
                    >
                        <InfoOutlined sx={{
                            fontSize: '10px', color: 'rgba(0, 0, 0, 0.6)'
                        }} /> {t('Quantitative')}
                    </Box>
                </Box>
                <Box className="widget-WIDGT_AIND_QUANT_02-right-content"
                    display={'flex'} flexDirection="column" gap={2} padding={'15px 5px'} 
                    width={'70%'} height={'100%'} boxSizing={'border-box'}
                >
                    <Box className='widget-WIDGT_AIND_QUANT_02-area-indicator-status-name'
                        fontFamily={'Inter'} fontSize={'17px'} fontWeight={'normal'}
                        color={'#011d89'}
                        display={'flex'} flexDirection={'row'} gap={0.5} alignItems={'center'}
                    >
                        <CombinedStatusTrend
                            status={areaIndicatorData.status} trend={areaIndicatorData.trend}
                            className={'widget-WIDGT_AIND_QUANT_02-area-indicator-status-trend'}
                        />
                        {areaIndicatorData.areaIndicatorName}
                    </Box>
                    <Box className='widget-WIDGT_AIND_QUANT_02-dates-progress' display={'flex'} flexDirection="row" gap={1} alignItems={'center'}>
                        <Box className='widget-WIDGT_AIND_QUANT_02-dates-progress-dates'
                            display={'flex'} flexDirection="column" gap={0.5} alignItems={'flex-start'}
                            width={'35%'} 
                        >
                            <WidgetLabelValue label={t('Start date:')} value={toLocaleDateTime(objectiveData.startDate, false)} />
                            <WidgetLabelValue label={t('End date:')} value={toLocaleDateTime(objectiveData.endDate, false)} />
                        </Box>
                        <Box width={'65%'} >
                            <SimpleTimeTargetProgress
                                targetProgress={objectiveData.progress}
                                timeProgress={calculateTimeProgress(objectiveData.startDate, objectiveData.endDate)}
                                showCalendarProgress={true}
                                showTargetProgress={false}
                                className={'widget-WIDGT_AIND_QUANT_02-dates-progress-progress'}
                            />
                        </Box>
                    </Box>
                    <Box className='widget-WIDGT_AIND_QUANT_02-dates-progress' display={'flex'} flexDirection="row" gap={1} alignItems={'center'}>
                        <Box className='widget-WIDGT_AIND_QUANT_02-dates-progress-dates'
                            display={'flex'} flexDirection="column" gap={0.5} alignItems={'flex-start'}
                            width={'35%'}
                        >
                            <Box className='widget-WIDGT_AIND_QUANT_02-objective-label' width={'100%'} display={'flex'} flexDirection="row" gap={0.5} alignItems={'center'}>
                                <WidgetLabelValue label={t('From: ')} value={objectiveData.startQuantitativeValue} />
                                <Box
                                    fontFamily={'Inter'} fontSize={'8px'} fontWeight={'normal'}
                                    color={'rgba(0, 0, 0, 0.6)'}
                                >{measurementUnit}</Box>
                            </Box>
                            <Box className='widget-WIDGT_AIND_QUANT_02-objective-label' width={'100%'} display={'flex'} flexDirection="row" gap={0.5} alignItems={'center'}>
                                <WidgetLabelValue label={t('To: ')} value={objectiveData.endQuantitativeValue} />
                                <Box
                                    fontFamily={'Inter'} fontSize={'8px'} fontWeight={'normal'}
                                    color={'rgba(0, 0, 0, 0.6)'}
                                >{measurementUnit}</Box>
                            </Box>
                        </Box>
                        <Box width={'65%'} >
                            <SimpleTimeTargetProgress
                                targetProgress={objectiveData.progress}
                                timeProgress={calculateTimeProgress(objectiveData.startDate, objectiveData.endDate)}
                                showCalendarProgress={false}
                                showTargetProgress={true}
                                className={'widget-WIDGT_AIND_QUANT_02-dates-progress-progress'}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        )
    );
}

export default WIDGT_AIND_QUANT_02_WidgetLayout;